{
  "fleet": {
    "ai": {
      "title": "GeoPoint AI",
      "description": "How GeoPoint AI is set up, who may use it, what they ask and what it costs.",
      "tab": {
        "settings": "Settings",
        "access": "Access",
        "conversations": "Conversations",
        "insights": "Insights"
      }
    },
    "aiInsights": {
      "from": "From",
      "to": "To",
      "show": "Show",
      "questions": "Questions",
      "cost": "Cost",
      "errors": "Errors",
      "errorRate": "Error rate",
      "errorsHint": "Errors: {{n}}",
      "fallbackRate": "Fallback rate",
      "avgTime": "Average answer time",
      "p95Time": "95th percentile time",
      "cacheHitRate": "Cache hit rate",
      "cacheHint": "cached ÷ input tokens",
      "questionsPerDay": "Questions per day",
      "costPerDay": "Cost per day",
      "tokensPerDay": "Tokens per day (in + out)",
      "byAccount": "By account",
      "tools": "Most used tools",
      "tool": "Tool",
      "calls": "Calls",
      "failingTools": "Tools that fail most",
      "noFailingTools": "No tool failed in this range.",
      "failShare": "{{errors}} of {{calls}} · {{percent}}",
      "refreshing": "Updating…",
      "problem": {
        "from": "Choose a first day.",
        "to": "Choose a last day.",
        "order": "The last day is before the first.",
        "span": "Choose a range of at most {{max}} days."
      },
      "feedbackPerDay": "👍 / 👎 per day",
      "day": "Day",
      "noFeedback": "No answer was rated in this range.",
      "openDown": "Open the conversations with a 👎"
    },
    "aiFailed": {
      "title": "Failed question",
      "question": "Question",
      "partial": "Partial answer",
      "noPartial": "Nothing was answered before it failed.",
      "error": "Error",
      "openConversation": "Open the conversation",
      "noConversation": "It failed before a conversation existed, so the user has no history of it."
    },
    "aiConversation": {
      "back": "Conversations",
      "title": "Conversation",
      "meta": "{{account}} · started {{when}}",
      "audited": "Opening this was recorded in Activity.",
      "openAccount": "Open the account",
      "user": "Question",
      "assistant": "GeoPoint AI",
      "turnError": "Failed",
      "timeline": "Timeline",
      "nextAttempt": "Next attempt",
      "toolCall": "Tool call: {{name}}",
      "toolInput": "Input",
      "toolOutput": "Output",
      "showAll": "Show all ({{count}} characters)",
      "turnsWithoutAnswer": "Turns without an answer",
      "failedQuestions": "Failed questions",
      "rating": {
        "up": "Good answer",
        "down": "Bad answer"
      }
    },
    "aiConversations": {
      "account": "Account",
      "anyAccount": "All accounts",
      "deletedAccount": "Deleted account #{{id}}",
      "from": "From",
      "to": "To",
      "tool": "Tool",
      "anyTool": "Any tool",
      "all": "All",
      "failed": "Failed",
      "slow": "Slow",
      "slowNote": "Slow applies to the conversations loaded so far.",
      "slowHint": "Conversations (total time of all answers) or failed questions of a minute or more, among those loaded",
      "none": "No conversations match these filters.",
      "more": "Load more",
      "fallback": "fallback",
      "ok": "OK",
      "errors": "Errors: {{n}}",
      "tokensTitle": "in {{input}} · cached {{cached}} · out {{output}} · reasoning {{reasoning}}",
      "col": {
        "when": "When",
        "account": "Account",
        "question": "Question",
        "turns": "Turns",
        "tokens": "Tokens in / cached / out / reasoning",
        "cost": "Cost",
        "model": "Model",
        "duration": "Time",
        "status": "Status",
        "rating": "Ratings"
      },
      "errorKind": {
        "timeout": "Timed out",
        "provider": "Provider error",
        "tools": "Tool error",
        "aborted": "Stopped",
        "other": "Error"
      },
      "down": "Bad answers",
      "downHint": "Conversations with at least one 👎 (failed questions are left out)",
      "ratingTitle": "👍 {{up}} · 👎 {{down}}"
    },
    "aiAccess": {
      "searchPlaceholder": "Name, email or account number…",
      "hint": "An empty limit means the default from Settings. The switch saves at once; a limit saves with its button.",
      "deleted": "Deleted account #{{id}}",
      "chip": {
        "all": "All",
        "on": "On",
        "off": "Off",
        "deleted": "Deleted accounts"
      },
      "col": {
        "account": "Account",
        "access": "Access and daily limit",
        "today": "Today",
        "month": "This month",
        "cost": "Cost this month"
      },
      "customersHint": "Accounts are grouped by customer: a manager and the accounts it manages, or a standalone account. A customer’s model and monthly budget apply to all its accounts; empty = the global settings and no budget of its own."
    },
    "aiSettings": {
      "changedElsewhere": "Settings were changed elsewhere. Reload to see them; your unsaved edits would be dropped.",
      "reload": "Reload",
      "noKeyWarning": "{{providers}} will have no key after this save, so GeoPoint AI cannot answer through it.",
      "spend": "Spend this month",
      "ofCap": "of the {{cap}} cap · {{percent}}",
      "noCap": "no monthly cap",
      "routing": "Models and limits",
      "primary": "Primary",
      "fallback": "Fallback",
      "noFallback": "No fallback",
      "monthlyCap": "Monthly cap (USD)",
      "defaultDailyLimit": "Questions per account per day",
      "invalid": "Check the marked fields.",
      "keySaved": "Key saved, ends in {{last4}}",
      "keyNone": "No key",
      "keyRemoving": "The key will be removed when you save.",
      "newKey": "New key",
      "check": "Check the saved key",
      "removeKey": "Remove key",
      "keepKey": "Keep key",
      "model": "Model",
      "modelNoPrice": "GeoPoint AI has no price for this model, so its cost could not be tracked. Priced models: {{models}}",
      "effort": "Reasoning",
      "effortLevel": {
        "minimal": "Minimal",
        "low": "Low",
        "medium": "Medium",
        "high": "High"
      },
      "note": {
        "xai": "Turn on Zero Data Retention for the xAI team the key belongs to.",
        "google": "The Gemini key must belong to a paid project (billing on); the free tier uses the data for training.",
        "anthropic": "Create the key in the Anthropic Console, in the organisation that should be billed."
      },
      "models": {
        "title": "Models (last 30 days)",
        "provider": "Provider",
        "questions": "Questions",
        "avg": "Average time",
        "p90": "90th percentile",
        "perQuestion": "Per question",
        "none": "No questions in the last 30 days."
      }
    },
    "ledger": {
      "title": "Ledger & health",
      "description": "The ride ledger behind every report, and whether the trackers and Traccar are healthy.",
      "tabLedger": "Ledger",
      "tabHealth": "Health",
      "status": "Ledger",
      "running": "Rebuilding",
      "idle": "Idle",
      "since": "Covers every day since {{day}}",
      "noStart": "No start day set",
      "progress": "{{done}} of {{total}} done · {{percent}}",
      "coverage": "Coverage",
      "coverageHint": "The days the ledger has stored for each vehicle. A filled green cell is a stored day; an outlined cell with a diagonal line is a missing day.",
      "cellBuilt": "{{day}}: stored",
      "cellMissing": "{{day}}: missing",
      "show": "Show",
      "from": "From",
      "to": "To",
      "noCoverage": "No stored days in this range.",
      "built": "stored",
      "missing": "missing",
      "missingCount": "{{n}} missing",
      "rebuild": "Rebuild",
      "rebuildHint": "Recompute one vehicle’s rides and report totals for up to {{max}} days. One rebuild runs at a time.",
      "vehicle": "Vehicle",
      "rebuildButton": "Rebuild…",
      "problem": {
        "from": "Choose a start day.",
        "to": "Choose an end day.",
        "order": "The end day is before the start day.",
        "span": "At most {{max}} days at once.",
        "future": "Today is not stored yet. Choose yesterday or earlier.",
        "beforeStart": "The ledger starts after that day."
      },
      "confirmTitle": "Rebuild {{name}}?",
      "confirmBody": "The rides and report totals from {{from}} to {{to}} ({{n}} days) are recomputed from the stored positions, so reports for these days may change. The progress shows here while it runs.",
      "confirm": "Rebuild",
      "switchedOff": "The ledger is switched off on this Traccar, so nothing can be rebuilt.",
      "vehicleHint": "Only vehicles that have reported are listed."
    },
    "health": {
      "silent": "Silent trackers",
      "silentWeek": "Over 7 days",
      "silentDay": "24 hours to 7 days",
      "noneSilent": "Every tracker reported in the last 24 hours.",
      "dataUsage": "Data usage per vehicle",
      "dataUsageOf": "Data per day — {{name}}",
      "vehicle": "Vehicle",
      "perDay": "Per day",
      "total": "Total",
      "statistics": "Traccar daily statistics",
      "messagesReceived": "Messages received",
      "messagesStored": "Messages stored",
      "activeDevices": "Active devices",
      "day": "Day"
    },
    "groups": {
      "tab": "Groups",
      "title": "Groups",
      "description": "Groups of vehicles and the accounts that see each group.",
      "searchPlaceholder": "Group name…",
      "col": {
        "name": "Group",
        "vehicles": "Vehicles",
        "accounts": "Accounts"
      },
      "detail": {
        "title": "Group",
        "vehicles": "Vehicles in this group",
        "accounts": "Accounts that see this group",
        "moveIn": "Move here",
        "moveOut": "Remove from group",
        "inGroup": "now in {{name}}",
        "moveHint": "Moving a vehicle here takes it out of the group it is in now."
      }
    },
    "zones": {
      "tab": "Zones",
      "title": "Zones",
      "description": "Every zone drawn in traccar-web, read-only here, and who each one is shared with.",
      "searchPlaceholder": "Zone name…",
      "none": "No zones yet",
      "sharedWith": "Shared with:",
      "sharedWithNobody": "nobody",
      "groupName": "group {{name}}",
      "shape": {
        "circle": "Circle",
        "polygon": "Area",
        "line": "Route",
        "unknown": "Not drawable"
      }
    },
    "vehicles": {
      "title": "Vehicles & trackers",
      "description": "Every vehicle and its tracker: whether it is reporting, its limits and counters, and who sees it.",
      "searchPlaceholder": "Plate, IMEI, model or group…",
      "noGroup": "No group",
      "noLimit": "No limit",
      "ago": "{{age}} ago",
      "never": "never",
      "card": {
        "total": "Vehicles",
        "online": "Online now",
        "silent": "Silent > 24 h",
        "noFix": "No fix today",
        "dataToday": "Data today"
      },
      "chip": {
        "all": "All",
        "online": "Online",
        "silent": "Silent > 24 h",
        "noFix": "No fix today",
        "noGroup": "No group"
      },
      "col": {
        "name": "Vehicle",
        "group": "Group",
        "status": "Last record",
        "speedLimit": "Speed limit",
        "fuel": "Fuel",
        "odometer": "Odometer",
        "accounts": "Accounts",
        "data7d": "Data 7 days"
      },
      "status": {
        "online": "Online",
        "offline": "Offline",
        "unknown": "Unknown"
      },
      "fuel": {
        "trusted": "Trusted",
        "approx": "Approximate",
        "none": "No fuel data",
        "unset": "Not set"
      },
      "detail": {
        "title": "Vehicle",
        "settings": "Settings",
        "speedLimit": "Speed limit (km/h)",
        "speedLimitHint": "{{min}}–{{max}} km/h, or empty for no limit.",
        "fuelQuality": "Fuel quality",
        "fuelHint": "How far GeoPoint AI and the reports may trust this vehicle’s fuel readings.",
        "accumulators": "Odometer and engine hours",
        "odometer": "Odometer (km)",
        "hours": "Engine hours (h)",
        "now": "Now {{value}}",
        "accumulatorsInvalid": "Use a number of 0 or more.",
        "accumulatorsSave": "Change…",
        "accumulatorsConfirmTitle": "Change the counters of {{name}}?",
        "accumulatorsConfirmBody": "Future report totals change: distance and engine hours count on from the new values. Reports already sent keep what they showed.",
        "accumulatorsConfirm": "Change counters",
        "changeLine": "{{field}}: {{from}} → {{to}}",
        "accumulatorsCanNote": "On vehicles with a CAN odometer the reports read the vehicle's own odometer, so this edit may not change the odometer in reports.",
        "noRecordYet": "This vehicle has sent no record yet, so its counters cannot be changed.",
        "group": "Group",
        "move": "Move",
        "lastFix": "Last fix",
        "lastRecords": "Last records received",
        "noRecords": "No records received yet.",
        "fixTime": "Fix time",
        "position": "Position",
        "speed": "Speed",
        "fix": "Fix",
        "valid": "valid",
        "invalid": "invalid",
        "accounts": "Accounts that see it"
      }
    },
    "accounts": {
      "tokens": {
        "title": "Customer app tokens",
        "description": "Let this customer's own app (e.g. damis) read and rename its vehicles through GeoPoint.",
        "empty": "No tokens yet.",
        "new": "New token",
        "newTitle": "New customer token",
        "newBody": "Name it after the app that will use it.",
        "label": "Label",
        "create": "Create",
        "createdTitle": "Copy the token now",
        "createdBody": "This is the only time it is shown. Store it as the app's secret (e.g. GPS_TOKEN in damis).",
        "copy": "Copy",
        "copyFailed": "Could not copy. Select the token and copy it by hand.",
        "copied": "Token copied",
        "done": "Done",
        "created": "Created {{day}}",
        "lastUsed": "last used {{day}}",
        "neverUsed": "never used",
        "revoked": "Revoked",
        "revoke": "Revoke",
        "revokeTitle": "Revoke {{label}}?",
        "revokeBody": "The app using it stops working until it gets a new token."
      },
      "title": "Accounts",
      "description": "Every Traccar account: who can sign in, what they see, and whether they use GeoPoint AI.",
      "searchPlaceholder": "Name or email…",
      "on": "Switched on",
      "off": "Switched off",
      "noExpiry": "Never expires",
      "questionsToday": "{{n}} today",
      "card": {
        "total": "Accounts",
        "disabled": "Switched off",
        "admins": "Admins and managers",
        "milo": "GeoPoint AI on"
      },
      "chip": {
        "all": "All",
        "on": "Switched on",
        "off": "Switched off",
        "admins": "Admins and managers",
        "milo": "GeoPoint AI on"
      },
      "col": {
        "name": "Name",
        "role": "Role",
        "vehicles": "Vehicles",
        "groups": "Groups",
        "status": "Account",
        "expiry": "Expires",
        "milo": "GeoPoint AI"
      },
      "role": {
        "admin": "Admin",
        "manager": "Manager",
        "user": "User",
        "readonly": "Read-only"
      },
      "detail": {
        "title": "Account",
        "vehicles": "Vehicles",
        "groups": "Groups",
        "access": "Sign-in",
        "accountSwitch": "Account switched on",
        "miloSwitch": "GeoPoint AI switched on",
        "dailyLimit": "Questions per day",
        "dailyLimitDefault": "default",
        "dailyLimitInvalid": "A whole number up to 1000, or empty for the default.",
        "conversations": "Conversations",
        "offTitle": "Switch off {{name}}?",
        "offBody": "They can no longer sign in to traccar-web or the app, and their reports and alerts stop reaching them, until the account is switched on again.",
        "offConfirm": "Switch off",
        "serviceAccount": "echo’s own Traccar account: switching it off would cut Fleet admin off from Traccar. Change it in traccar-web if you really must."
      }
    },
    "sims": {
      "title": "SIM data",
      "description": "What 1NCE bills each tracker SIM per day, and how much of its allowance is left.",
      "syncNow": "Sync now",
      "syncing": "Syncing…",
      "live": "Live",
      "liveAt": "Live · {{time}}",
      "lastRun": "Last sync {{when}} · {{status}} · {{count}} SIMs",
      "neverRun": "Not synced yet. The first sync fetches every SIM’s history.",
      "running": "Syncing now…",
      "status": {
        "ok": "ok",
        "partial": "partly",
        "failed": "failed",
        "running": "running"
      },
      "openSince": "Open since {{when}} — not booked yet",
      "closed": "Closed {{when}}",
      "unknownSession": "—",
      "lastError": "Last check failed: {{error}}",
      "days": {
        "day": "Day",
        "volume": "Used",
        "sent": "Sent",
        "received": "Received",
        "state": "State",
        "empty": "No days in this range."
      },
      "state": {
        "confirmed": "Confirmed",
        "checking": "Checking",
        "changed": "{{change}} on re-check"
      },
      "summary": {
        "used": "Used in {{period}}: {{used}}",
        "perVehicle": "Per vehicle a day: {{value}}",
        "low": "Under 20 % left: {{count}}",
        "open": "Not booked yet: {{count}}"
      },
      "card": {
        "leftOf": "left of {{total}}",
        "lasts": "Lasts until ≈ {{year}}, past the SIM’s expiry ({{expiry}})",
        "runsOut": "Runs out ≈ {{year}}, before the SIM expires ({{expiry}})",
        "lastsNoExpiry": "Lasts until ≈ {{year}}",
        "noEstimate": "No recent use to estimate from",
        "used": "{{used}} in {{period}}, {{perDay}} a day",
        "bars": "Data per day: {{name}}",
        "notFinal": "not final",
        "open": "Open the SIM of {{name}}",
        "details": "Details"
      },
      "noVehicles": "No SIM is in a vehicle yet.",
      "spares": "Spare SIMs, not in a vehicle: {{count}}",
      "spareLeft": "{{left}} of {{total}} left",
      "by": {
        "day": "Days",
        "week": "Weeks",
        "month": "Months"
      },
      "periods": {
        "day": "the last 30 days",
        "week": "the last 12 weeks",
        "month": "the last 12 months"
      },
      "panel": {
        "phone": "Phone number",
        "phoneHint": "SMS commands to the tracker go to this number",
        "imei": "Device IMEI",
        "imsi": "IMSI",
        "iccid": "ICCID",
        "ip": "IP address",
        "status": "Status",
        "activated": "Activated",
        "validUntil": "Valid until",
        "left": "Data left",
        "leftOf": "{{left}} of {{total}}",
        "session": "Session",
        "usage": "Usage",
        "week": "Week",
        "month": "Month",
        "weekOf": "Week of {{date}}"
      },
      "heatmap": {
        "title": "Usage by vehicle",
        "vehicle": "Vehicle",
        "total": "Total",
        "cell": "{{name}}, {{period}}: {{value}}",
        "noData": "no data",
        "zero": "0",
        "upTo": "up to {{value}}"
      }
    },
    "nav": {
      "title": "Fleet admin",
      "accounts": "Accounts",
      "vehicles": "Vehicles",
      "groups": "Groups & zones",
      "ledger": "Ledger & health",
      "sims": "SIM data",
      "ai": "GeoPoint AI"
    },
    "common": {
      "eyebrow": "Fleet admin",
      "timesIn": "All times {{tz}}",
      "unavailable": "unavailable",
      "unavailableHint": "This could not be loaded just now",
      "loading": "Loading…",
      "retry": "Try again",
      "readOnly": "Showing the last data read. Changes are paused until the connection is back.",
      "openInTraccar": "Open in traccar-web",
      "search": "Search",
      "cancel": "Cancel",
      "save": "Save",
      "saving": "Saving…",
      "on": "On",
      "off": "Off",
      "none": "None",
      "all": "All",
      "noMatch": "Nothing matches “{{query}}”",
      "noMatchHint": "Try a name, a plate, an email or an IMEI.",
      "empty": "Nothing here yet",
      "link": "Link",
      "unlink": "Unlink",
      "locked": "Locked",
      "nothingToLink": "Nothing left to link",
      "lockedWhy": "echo’s own service account",
      "pick": "Choose…",
      "noneLinked": "Nothing linked",
      "back": "Back"
    },
    "errors": {
      "traccar_not_connected": "Traccar is not connected to echo yet.",
      "traccar_unavailable": "Traccar is not answering. Showing the last data read; try again in a minute.",
      "traccar_auth": "Traccar refused echo’s token. The Traccar connection needs a new token.",
      "milo_not_connected": "GeoPoint AI is not connected to echo: its address or admin token is missing or not accepted.",
      "milo_unavailable": "GeoPoint AI is not answering. Try again in a minute.",
      "not_found": "It no longer exists. The list has been refreshed.",
      "validation": "Some values were not accepted. Check the form and try again.",
      "rebuild_running": "A rebuild is already running. Try again when it has finished.",
      "once_not_connected": "SIM data is not connected to echo yet: 1NCE’s API key is missing.",
      "once_unavailable": "1NCE is not answering. Showing the numbers from the last sync; try again in a minute.",
      "once_auth": "1NCE refused echo’s API key. The 1NCE connection needs a new key.",
      "sync_running": "A SIM sync is already running. Its numbers show up when it has finished.",
      "network": "echo cannot be reached. Check your connection and try again.",
      "unknown": "Something went wrong. Try again."
    },
    "warnings": {
      "title": "Some parts could not be loaded",
      "parts": "Missing: {{parts}}",
      "part": {
        "accounts": "account names",
        "links": "links between accounts, vehicles and groups",
        "groups": "group names",
        "devices": "vehicle names",
        "positions": "latest records",
        "dataUsage": "data usage",
        "milo": "GeoPoint AI",
        "ledgerStatus": "ledger status",
        "ledgerCoverage": "ledger coverage",
        "statistics": "Traccar statistics",
        "zoneSharing": "zone sharing",
        "customers": "customers (each account is shown as its own customer)"
      }
    },
    "notConnected": {
      "traccarTitle": "Traccar is not connected",
      "traccarBody": "echo needs TRACCAR_URL and TRACCAR_SERVICE_TOKEN (the token of the Traccar admin account echo-service). Set both in echo’s environment and restart echo.",
      "miloTitle": "GeoPoint AI is not connected",
      "miloBody": "echo needs MILO_URL and MILO_ADMIN_TOKEN, and Milo needs the same MILO_ADMIN_TOKEN. Set them and restart both.",
      "onceTitle": "SIM data is not connected",
      "onceBody": "echo needs ONCE_CLIENT_ID and ONCE_CLIENT_SECRET (a 1NCE Management API key). Set both in echo’s environment and restart echo."
    },
    "aiCustomers": {
      "unknown": "Customer #{{id}}",
      "deleted": "Deleted customer #{{id}}",
      "members": "Accounts: {{n}} · GeoPoint AI on: {{enabled}}",
      "provider": "Customer model",
      "model": "Model",
      "budget": "Monthly budget (USD)",
      "noBudget": "No budget",
      "globalModel": "Global model",
      "globalModelIs": "Global model ({{model}} · {{effort}})",
      "noKey": "{{provider}} (no key)",
      "spendOfBudget": "{{spent}} of {{budget}} · {{percent}}",
      "spendNoBudget": "{{spent}} this month · no budget of its own",
      "questions": "Questions this month: {{n}}",
      "noLonger": "This account is no longer a customer (another account manages it now): its own model and budget do not apply.",
      "state": {
        "near": "Near the budget",
        "reached": "Budget used up: paused until the month ends"
      },
      "invalid": {
        "provider": "{{provider}} has no key, so this customer runs on the global model. Choose another model or the global one.",
        "model": "GeoPoint AI has no price for this model. Choose a priced one.",
        "effort": "Choose a reasoning level.",
        "budget": "A budget is an amount in USD from 0 to 100,000; empty = no budget of its own."
      },
      "lock": {
        "customers": "Customers could not be loaded, so models and budgets cannot be changed now.",
        "unknown": "This customer is not on the customer list yet. Reload to change it.",
        "deleted": "This customer's account is gone; its settings no longer apply.",
        "settings": "GeoPoint AI settings are not loaded, so no model can be chosen."
      },
      "noMembers": "No accounts"
    }
  },
  "sidebar": {
    "title": "Sidebar",
    "mobileDescription": "Displays the mobile sidebar.",
    "toggle": "Toggle Sidebar"
  },
  "nav": {
    "board": "Board",
    "planner": "Planner",
    "map": "Live map",
    "exceptions": "Exceptions",
    "documents": "Paperwork",
    "buyers": "Buyers",
    "fleet": "Fleet",
    "billing": "Invoicing",
    "outbox": "Outbox",
    "roadmap": "Roadmap",
    "dashboard": "Dashboard",
    "counters": "Counters",
    "users": "Users",
    "activityHistory": "Activity",
    "profile": "Profile",
    "settings": "Settings",
    "collapse": "Collapse",
    "demo": "Demo",
    "logout": "Logout",
    "section": {
      "dispatch": "Dispatch",
      "book": "Book",
      "admin": "Admin"
    }
  },
  "theme": {
    "light": "Light",
    "dark": "Dark",
    "system": "System"
  },
  "language": {
    "en": "English",
    "es": "Español",
    "sr": "Српски"
  },
  "common": {
    "user": "User",
    "cancel": "Cancel",
    "save": "Save",
    "dismiss": "Dismiss"
  },
  "login": {
    "title": "Welcome back",
    "description": "Enter your credentials to sign in to your account",
    "identifier": {
      "username": "Username",
      "email": "Email",
      "both": "Username or email",
      "placeholder": "Enter your {{field}}"
    },
    "password": {
      "label": "Password",
      "placeholder": "Enter your password"
    },
    "button": "Sign in",
    "button.loading": "Signing in...",
    "error": {
      "identifierRequired": "{{field}} is required",
      "passwordLength": "Password must be at least 6 characters",
      "failed": "Login failed",
      "unknown": "An unexpected error occurred"
    },
    "forgotPassword": "Forgot password?",
    "noAccount": "Don't have an account?",
    "signUp": "Sign up"
  },
  "invitation": {
    "title": "Complete Your Registration",
    "description": "Set your password to activate your account",
    "username": "Username",
    "password": "Password",
    "passwordPlaceholder": "Enter your password",
    "confirmPassword": "Confirm Password",
    "confirmPasswordPlaceholder": "Confirm your password",
    "button": "Set Password & Activate",
    "button.loading": "Setting up...",
    "invalidTitle": "Invalid Invitation",
    "goToLogin": "Go to Login",
    "resetPassword": {
      "title": "Reset Your Password",
      "description": "Enter a new password for your account",
      "button": "Reset Password",
      "button.loading": "Resetting..."
    },
    "error": {
      "noToken": "No invitation token provided",
      "invalidToken": "Invalid or expired invitation token",
      "passwordLength": "Password must be at least 8 characters",
      "passwordMismatch": "Passwords do not match",
      "failed": "Failed to set password. Please try again."
    }
  },
  "profile": {
    "title": "My Account",
    "subtitle": "Manage your profile and security settings",
    "edit": "Edit",
    "verified": "Verified",
    "updateSuccess": "Profile updated successfully",
    "updateError": "Failed to update profile",
    "info": {
      "title": "Profile Information",
      "description": "Your account details"
    },
    "field": {
      "name": "Full Name",
      "username": "Username",
      "email": "Email",
      "memberSince": "Member Since"
    },
    "editModal": {
      "title": "Edit Profile",
      "description": "Update your profile information"
    },
    "security": {
      "title": "Change Password",
      "description": "Update your password to keep your account secure",
      "currentPassword": "Current Password",
      "newPassword": "New Password",
      "confirmPassword": "Confirm New Password",
      "passwordMismatch": "Passwords do not match",
      "passwordInvalid": "Password does not meet requirements",
      "changeSuccess": "Password changed successfully. Other sessions have been logged out.",
      "changeError": "Failed to change password",
      "changeButton": "Change Password"
    },
    "sessions": {
      "currentSession": "Current Session",
      "current": "Current",
      "device": "Device",
      "ip": "IP Address",
      "lastActive": "Last Active",
      "now": "Now",
      "unknown": "Unknown",
      "otherSessions": "Other Sessions",
      "otherDescription": "Sessions active on other devices",
      "revokeAll": "Revoke All",
      "revoke": "Revoke",
      "revokeSuccess": "Session revoked successfully",
      "revokeError": "Failed to revoke session",
      "revokeAllSuccess": "All other sessions have been logged out",
      "revokeAllError": "Failed to revoke sessions",
      "noOtherSessions": "No other active sessions",
      "lastActiveAt": "Last active {{time}}",
      "neverActive": "Never active",
      "confirmRevokeAll": {
        "title": "Revoke All Sessions",
        "description": "This will log out all other devices. You will remain logged in on this device."
      },
      "time": {
        "daysAgo": "{{count}}d ago",
        "hoursAgo": "{{count}}h ago",
        "justNow": "just now",
        "minutesAgo": "{{count}}m ago"
      },
      "unknownBrowser": "Unknown browser",
      "unknownDevice": "Unknown device"
    }
  },
  "settings": {
    "title": "Settings",
    "subtitle": "Manage your application preferences",
    "theme": "Theme",
    "themeDescription": "Select your preferred theme",
    "language": "Language",
    "languageDescription": "Select your preferred language"
  },
  "users": {
    "title": "Users",
    "button": {
      "add": "Add User",
      "resetPassword": "Reset Password",
      "resendInvitation": "Resend Invitation",
      "deactivate": "Deactivate",
      "activate": "Activate",
      "demoteFromAdmin": "Demote from Admin",
      "promoteToAdmin": "Promote to Admin",
      "delete": "Delete"
    },
    "table": {
      "username": "Username",
      "email": "Email",
      "name": "Name",
      "role": "Role",
      "status": "Status",
      "lastLogin": "Last Login",
      "created": "Created",
      "actions": "Actions",
      "noResults": "No users found",
      "roles": "Roles"
    },
    "status": {
      "active": "Active",
      "inactive": "Inactive"
    },
    "date": {
      "never": "Never"
    },
    "actions": {
      "label": "Actions",
      "copyId": "Copy user ID",
      "editUser": "Edit user",
      "openMenu": "Open menu",
      "viewDetails": "View details"
    },
    "filter": {
      "allRoles": "All Roles",
      "allStatus": "All Status"
    },
    "search": {
      "placeholder": "Search by username..."
    },
    "error": {
      "create": "Failed to create user",
      "delete": "Failed to delete user",
      "resetPassword": "Failed to reset password",
      "resendInvitation": "Failed to resend invitation",
      "activate": "Failed to activate user",
      "demote": "Failed to demote user",
      "promote": "Failed to promote user",
      "deactivate": "Failed to deactivate user"
    },
    "role": {
      "USER": "User",
      "ADMIN": "Admin",
      "SUPERADMIN": "Super Admin"
    },
    "modal": {
      "add": {
        "title": "Add New User",
        "description": "Create a new user account. An invitation link will be generated.",
        "name": "Name",
        "namePlaceholder": "Enter full name...",
        "email": "Email",
        "emailPlaceholder": "Enter email...",
        "emailOptional": "Email (optional)",
        "identifierHint": "Enter a username, an email, or both — the user signs in with whichever you set.",
        "username": "Username",
        "usernamePlaceholder": "Enter username...",
        "role": "Role",
        "password": "Password (optional)",
        "passwordPlaceholder": "Leave empty to send invitation link",
        "passwordHint": "If left empty, an invitation link will be generated for the user to set their own password.",
        "button": "Create User",
        "rolePlaceholder": "Select a role"
      },
      "invitation": {
        "copied": "Link copied",
        "copyFailed": "Could not copy the link. Copy it manually.",
        "title": "Invitation Link",
        "message": "Share this link with the user to complete their registration.",
        "emailSent": "The invitation was emailed to {{email}}.",
        "emailFailed": "The invitation could not be emailed to {{email}}. Send the link below yourself.",
        "button": {
          "copy": "Copy Link",
          "done": "Done"
        }
      },
      "deactivate": {
        "title": "Deactivate User",
        "message": "Are you sure you want to deactivate {{username}}?",
        "button": "Deactivate"
      },
      "selfReset": {
        "title": "Reset Your Password",
        "message": "You are about to reset your own password. You will be logged out and need to use the invitation link to log back in.",
        "button": "Reset Password"
      },
      "button": {
        "cancel": "Cancel"
      },
      "delete": {
        "title": "Delete User",
        "message": "This deletes {{username}} for good and cannot be undone. The account is signed out everywhere and can never sign in again, and its username and email are freed for someone new. To only take access away, deactivate instead.",
        "confirm": "Type {{username}} to confirm",
        "button": "Delete User"
      }
    },
    "card": {
      "total": "total"
    },
    "pagination": {
      "of": "of",
      "page": "Page",
      "rowsPerPage": "Rows per page"
    }
  },
  "counters": {
    "title": "Counters",
    "description": "Manage your counters",
    "newCounter": "New Counter",
    "empty": "No counters yet. Create one to get started!",
    "lastUpdated": "Last updated:",
    "modal": {
      "title": "Create New Counter",
      "description": "Enter a name for your new counter.",
      "nameLabel": "Counter Name",
      "namePlaceholder": "Enter counter name...",
      "cancel": "Cancel",
      "create": "Create"
    },
    "clearAll": "Clear All",
    "clearAllModal": {
      "cancel": "Cancel",
      "confirm": "Delete All",
      "description": "Are you sure you want to delete all {{count}} counters? This action cannot be undone.",
      "title": "Clear All Counters"
    },
    "error": {
      "create": "Failed to create counter",
      "decrement": "Failed to decrement counter",
      "delete": "Failed to delete counter",
      "deleteAll": "Failed to delete all counters",
      "increment": "Failed to increment counter",
      "reset": "Failed to reset counter"
    },
    "loadError": "Could not load counters.",
    "noResults": "No counters match your search.",
    "search": "Search counters...",
    "toast": {
      "incremented": "Counter incremented"
    },
    "total": "total"
  },
  "activity": {
    "table": {
      "timestamp": "Timestamp",
      "action": "Action",
      "entityType": "Entity Type",
      "user": "User",
      "ip": "IP Address",
      "status": "Status",
      "noResults": "No activity logs found"
    },
    "status": {
      "success": "Success",
      "failed": "Failed"
    },
    "filter": {
      "clear": "Clear",
      "allActions": "All Actions",
      "allEntities": "All Entities",
      "allStatus": "All Status"
    },
    "entityType": {
      "session": "Session",
      "user": "User",
      "counter": "Counter"
    },
    "action": {
      "LOGIN": "Login",
      "LOGIN_FAILED": "Login Failed",
      "LOGOUT": "Logout",
      "LOGOUT_ALL": "Logout All",
      "TOKEN_REFRESH": "Token Refresh",
      "CREATE": "Create",
      "UPDATE": "Update",
      "DELETE": "Delete",
      "PASSWORD_SET": "Password Set",
      "PASSWORD_CHANGE": "Password Change",
      "PASSWORD_RESET": "Password Reset",
      "REGISTER": "Register",
      "EMAIL_VERIFY": "Email Verify",
      "VIEW": "View",
      "RESEND_VERIFICATION": "Resend Verification"
    },
    "search": {
      "placeholder": "Search by username, IP, entity..."
    },
    "button": {
      "details": "Details"
    },
    "modal": {
      "title": "Activity Details"
    },
    "details": {
      "error": "Error",
      "previousState": "Previous State",
      "newState": "New State",
      "metadata": "Metadata",
      "userAgent": "User Agent"
    },
    "pagination": {
      "of": "of",
      "page": "Page",
      "rowsPerPage": "Rows per page"
    }
  },
  "activityHistory": {
    "title": "Activity History",
    "description": "View system audit logs and user activity",
    "card": {
      "total": "entries"
    }
  },
  "toast": {
    "sessionExpired": "Session expired. Please log in again.",
    "deviceMismatch": "This session belongs to another device. Please log in again.",
    "rateLimited": "Too many requests. Please try again later.",
    "reconnecting": "Reconnecting…",
    "unreachable": "We couldn't reach the server — it may be restarting. Nothing was saved; please try again in a moment."
  },
  "error500": {
    "title": "500 Server Error",
    "subtitle": "Something went wrong on our end",
    "description": "We apologize for the inconvenience. The server encountered an internal error and was unable to complete your request. Our team has been notified.",
    "tryAgain": "Try Again",
    "goHome": "Go Home",
    "errorCode": "Error code"
  },
  "backend": {
    "accountLocked": "Too many failed login attempts. Try again in {{seconds}} seconds",
    "accountLockedTitle": "Account temporarily locked",
    "badRequest": "The request was invalid",
    "cannotAssignCustomerRole": "The Customer role cannot be assigned to admin-managed users",
    "cannotDeactivateOwnAccount": "You cannot deactivate your own account",
    "cannotDeleteOwnAccount": "You cannot delete your own account",
    "cannotDeleteSuperAdmin": "Super Admin users cannot be deleted",
    "cannotModifyOwnRoles": "You cannot modify your own roles",
    "cannotModifyOwnSuperAdmin": "You cannot modify your own Super Admin status",
    "cannotResendSuperAdminInvitation": "Invitations cannot be resent for Super Admin users",
    "cannotResetSuperAdminPassword": "The password of a Super Admin cannot be reset here",
    "cannotUpdateSuperAdmin": "Super Admin users cannot be updated",
    "conflict": "This already exists",
    "counterNotFound": "Counter not found",
    "currentPasswordIncorrect": "Current password is incorrect",
    "emailAlreadyVerified": "This email is already verified. You can log in.",
    "emailExists": "This email is already registered",
    "emailNotVerified": "Please verify your email before logging in",
    "emailRequired": "Email is required",
    "fetchActivityFailed": "Could not load activity",
    "fetchProfileFailed": "Could not load your profile",
    "fetchSessionsFailed": "Could not load your sessions",
    "fetchUsersFailed": "Could not load users",
    "forbidden": "You don't have permission to do that",
    "identifierInUse": "That username or email is already in use",
    "identifierRequired": "Username or email is required",
    "invalidCredentials": "Invalid username or password",
    "invalidEmail": "Invalid email format",
    "invalidInvitationToken": "Invalid or expired invitation link",
    "invalidResetToken": "This password reset link is invalid or has expired",
    "invalidVerificationToken": "This verification link is invalid or has expired",
    "logoutAllFailed": "Could not log out the other sessions",
    "logoutFailed": "Logout failed",
    "newPasswordSameAsCurrent": "New password must be different from current password",
    "noPlatformAccess": "Your account does not have access to this application",
    "notFound": "Not found",
    "onlySuperAdminsCreateSuperAdmins": "Only Super Admins can create Super Admin users",
    "onlySuperAdminsManageSuperAdmins": "Only Super Admins can manage Super Admin status",
    "passwordAlreadySet": "This user has already set a password. Use password reset instead.",
    "passwordComplexity": "Password does not meet requirements",
    "passwordMinLength": "Password must be at least {{count}} characters",
    "passwordNotSet": "This account has no password set",
    "passwordSetupRequired": "You must set your password before logging in",
    "profileNotFound": "Profile not found",
    "registrationFailed": "Registration failed",
    "registrationUnavailable": "Registration is not available",
    "resetPasswordFailed": "Failed to reset password",
    "revokeSessionFailed": "Could not revoke the session",
    "securityCheckFailed": "Security check failed. Please reload the page and try again.",
    "serverError": "Something went wrong on our side. Please try again.",
    "sessionNotFound": "Session not found",
    "tooManyLoginAttempts": "Too many login attempts. Try again in {{seconds}} seconds",
    "updateUserFailed": "Failed to update user",
    "userNotFound": "User not found",
    "usernameDerivedFromEmail": "The username is derived from the email when login is by email",
    "usernameExists": "Username already exists",
    "usernameRequired": "Username is required",
    "verificationFailed": "Verification failed",
    "verificationUnavailable": "Email verification is not available",
    "verifyInvitationFailed": "Could not verify the invitation",
    "accountDeactivated": "Account is deactivated",
    "accountLockedFor": "Account locked for {{seconds}} seconds",
    "invalidPassword": "Invalid password",
    "invalidPasswordLocked": "Invalid password – account locked for {{seconds}} seconds",
    "passwordPolicyDefault": "Password must contain uppercase, lowercase, number, and special character",
    "platformAccessDenied": "Access denied for this platform ({{platform}})",
    "sendVerificationFailed": "The verification email could not be sent"
  },
  "dataTable": {
    "clearFilters": "Clear filters",
    "columns": "Columns",
    "noResults": "No results.",
    "pagination": {
      "next": "Next",
      "pageOf": "Page {{page}} of {{pageCount}}",
      "perPage": "{{count}} / page",
      "previous": "Previous",
      "showing": "Showing {{from}} to {{to}} of {{total}} results"
    },
    "rowsSelected": "{{selected}} of {{total}} row(s) selected.",
    "searchPlaceholder": "Search...",
    "column": {
      "actions": "Actions",
      "createdAt": "Created",
      "isActive": "Status",
      "lastLoginAt": "Last Login",
      "name": "Name",
      "roles": "Roles",
      "username": "Username"
    }
  },
  "forgotPassword": {
    "backToLogin": "Back to login",
    "description": "Enter your email and we will send you a reset link.",
    "email": "Email",
    "emailPlaceholder": "Enter your email",
    "sent": {
      "description": "If an account exists with this email, you will receive a reset link.",
      "title": "Check your email"
    },
    "submit": "Send Reset Link",
    "title": "Forgot Password"
  },
  "password": {
    "error": {
      "complexity": "Password does not meet requirements",
      "minLength": "Password must be at least {{count}} characters"
    },
    "requirement": {
      "complexity": "Meets complexity requirements",
      "minLength": "At least {{count}} characters"
    },
    "show": "Show password",
    "hide": "Hide password"
  },
  "register": {
    "button": "Create account",
    "button.loading": "Creating account...",
    "confirmPassword": {
      "label": "Confirm Password",
      "placeholder": "Confirm your password"
    },
    "description": "Enter your details to create your account",
    "email": {
      "label": "Email",
      "placeholder": "Enter your email"
    },
    "error": {
      "failed": "Registration failed. Please try again.",
      "invalidEmail": "Please enter a valid email address",
      "nameRequired": "Name is required",
      "passwordLength": "Password must be at least 8 characters",
      "passwordMismatch": "Passwords do not match"
    },
    "hasAccount": "Already have an account?",
    "name": {
      "label": "Name",
      "placeholder": "Enter your name"
    },
    "password": {
      "label": "Password",
      "placeholder": "Enter your password"
    },
    "signIn": "Sign in",
    "success": "Registration successful! Please check your email to verify your account.",
    "title": "Create an account"
  },
  "resetPassword": {
    "confirmPassword": "Confirm Password",
    "description": "Enter your new password below.",
    "error": "Failed to reset password",
    "goToLogin": "Go to Login",
    "invalid": {
      "description": "This password reset link has expired or is invalid. Please request a new one.",
      "title": "Invalid or Expired Link"
    },
    "newPassword": "New Password",
    "passwordInvalid": "Password does not meet requirements",
    "passwordMismatch": "Passwords do not match",
    "requestNew": "Request New Link",
    "submit": "Reset Password",
    "success": "Password reset successfully",
    "success.description": "You will be redirected to login shortly.",
    "success.title": "Password Reset Successfully",
    "title": "Reset Password"
  },
  "verifyEmail": {
    "error": {
      "invalidToken": "This verification link is invalid or has expired.",
      "noToken": "No verification token provided",
      "title": "Verification Failed"
    },
    "goToLogin": "Go to Login",
    "needAccount": "Don't have an account?",
    "resend": {
      "button": "Resend Verification Email",
      "cancel": "Cancel",
      "email": {
        "label": "Email Address",
        "placeholder": "Enter your email"
      },
      "emailRequired": "Please enter your email",
      "sending": "Sending...",
      "submit": "Send",
      "success": "If your email is registered, you will receive a verification email."
    },
    "signUp": "Sign up",
    "success": {
      "description": "Your email has been verified successfully. You can now log in to your account.",
      "redirect": "Redirecting to login...",
      "title": "Email Verified!"
    }
  },
  "unsavedChanges": {
    "title": "Leave without saving?",
    "body": "Your changes will be lost.",
    "stay": "Stay",
    "leave": "Leave"
  },
  "richText": {
    "bold": "Bold",
    "italic": "Italic",
    "underline": "Underline",
    "strike": "Strikethrough",
    "heading": "Heading",
    "paragraph": "Paragraph",
    "bullets": "Bulleted list",
    "numbers": "Numbered list",
    "quote": "Quote"
  }
}
